import axios from 'axios'
import store from '@/store'
import { usesFirebaseSession } from './session-config'
import { installSessionHttp } from './session-http'
import { i18n } from '@/locales/i18n-setup'
import { GENERIC_API_ERROR, isHtmlErrorBody } from '@/utils/apiErrors'

const apiBase =
  process.env.VUE_APP_API_URL ||
  (typeof window !== 'undefined' && window.VUE_APP_API_URL) ||
  ''
axios.defaults.baseURL = apiBase

function normalizeApiError(data) {
  const defaultResult = { message: GENERIC_API_ERROR, fieldErrors: {} }
  if (!data) return defaultResult

  // An HTML body means the request never reached a DRF handler (unhandled 500,
  // gateway fault). It has no field errors and no message fit for display.
  if (isHtmlErrorBody(data)) {
    return { message: GENERIC_API_ERROR, fieldErrors: {}, isUnexpected: true }
  }

  if (typeof data === 'string' || data instanceof String) {
    return { message: String(data), fieldErrors: {} }
  }

  if (data.detail) {
    return { message: data.detail, fieldErrors: {} }
  }

  if (Array.isArray(data.non_field_errors) && data.non_field_errors.length) {
    return { message: data.non_field_errors[0], fieldErrors: {} }
  }

  if (typeof data === 'object') {
    const entries = Object.entries(data)

    if (entries.length > 0) {
      const [field, value] = entries[0]

      if (Array.isArray(value) && value.length > 0) {
        return { message: value[0], fieldErrors: { [field]: value } }
      }

      if (typeof value === 'string') {
        return { message: value, fieldErrors: { [field]: [value] } }
      }
    }
  }

  if (data.error) {
    return { message: String(data.error), fieldErrors: {} }
  }

  if (data.message) {
    return { message: String(data.message), fieldErrors: {} }
  }

  try {
    return { message: JSON.stringify(data), fieldErrors: {} }
  } catch {
    return defaultResult
  }
}

function translateResponse(response) {
  const translated = _translate(response)

  return translated
}

function _translate(item) {
  if (typeof item === 'string' || item instanceof String) {
    return i18n.t(item)
  } else if (item instanceof Array) {
    return item.map(subItem => _translate(subItem))
  } else if (item instanceof Object) {
    return Object.fromEntries(
      Object.entries(item).map(([key, value]) => [key, _translate(value)])
    )
  } else {
    return item
  }
}

axios.interceptors.response.use(
  response => {
    if (!usesFirebaseSession() && !response.data.bypassRefresh) {
      store.commit('setLastActivity')
    }

    return response
  },
  async error => {
    if (usesFirebaseSession()) return Promise.reject(error)
    const original = error?.config
    const status = error?.response?.status
    const isRefreshCall = original?.url?.includes('/api/token/refresh')

    if (status === 401 && original && !original.__isRetry && !isRefreshCall) {
      original.__isRetry = true
      try {
        await store.dispatch('refreshAccess')
        const newAccess = store.state.authStore?.accessToken
        if (!newAccess) {
          store.dispatch('logout')

          return Promise.reject(error)
        }
        original.headers = original.headers || {}
        original.headers.Authorization = `Bearer ${newAccess}`

        return axios(original)
      } catch {
        store.dispatch('logout')

        return Promise.reject(error)
      }
    }

    if (status === 401) {
      store.dispatch('logout')
    } else if (isHtmlErrorBody(error?.response?.data)) {
      // An HTML body is a server/gateway fault, not an API error. Keep the raw
      // page for debugging but hand views the DRF shape they expect — passing
      // the markup through would have each caller flatten it per-character.
      error.rawResponseData = error.response.data
      const normalized = normalizeApiError(error.response.data)
      error.response.data = { detail: normalized.message }
      error.normalized = normalized
      error.message = normalized.message
      console.error(
        `Unexpected HTML error response (${status ?? 'no status'}) from ${
          original?.url ?? 'unknown URL'
        }`,
        error.rawResponseData
      )
    } else if (error?.response?.data) {
      const translated = translateResponse(error.response.data)
      error.response.data = translated
      const normalized = normalizeApiError(translated)
      error.normalized = normalized
      if (normalized?.message) {
        error.message = normalized.message
      }
    }

    return Promise.reject(error)
  }
)

axios.interceptors.request.use(config => {
  if (
    !store?.state?.dataStore?.currentMerchant ||
    !store.state.dataStore.currentMerchant.integration_type
  ) {
    return config
  }
  const integrationType =
    store?.state?.dataStore?.currentMerchant?.integration_type
  if (integrationType) {
    config.headers = {
      'X-Blytz-Integration-Type': integrationType,
      ...config.headers
    }
  }

  return config
})

export default axios

export const cfAxios = axios.create({
  // process.env wins for local dev (VUE_APP_AUTH_SERVICE_URL is injected by the local dev
  // harness so cfAxios targets the local gateway); the window value is the deployed gateway
  // URL from env-config.js. Mirrors how axios.defaults.baseURL is resolved above.
  baseURL:
    process.env.VUE_APP_AUTH_SERVICE_URL ||
    (typeof window !== 'undefined' && window.VUE_APP_AUTH_SERVICE_URL) ||
    ''
})

installSessionHttp(axios)
installSessionHttp(cfAxios)

cfAxios.interceptors.request.use(config => {
  if (usesFirebaseSession()) return config
  // The Firebase token is supplied by the Django login/refresh response and
  // stored in the auth module; it is kept fresh by the Django-driven refresh
  // cycle. Attach it for auth-service calls, falling back to the Django JWT.
  const firebaseToken = store?.state?.authStore?.firebaseToken
  if (firebaseToken) {
    config.headers.Authorization = `Bearer ${firebaseToken}`

    return config
  }
  const djangoAuth = axios.defaults.headers.common['Authorization']
  if (djangoAuth) {
    config.headers.Authorization = djangoAuth
  }

  return config
})

export const anonymousAxios = axios.create({
  baseURL: axios.defaults.baseURL,
  transformRequest: [
    (data, headers) => {
      // or just the auth header
      if (headers.common) delete headers.common.Authorization
      delete headers.Authorization
      delete headers.authorization

      return data
    },
    ...axios.defaults.transformRequest
  ]
})

export const anonymousAuthAxios = axios.create({
  baseURL: cfAxios.defaults.baseURL
})
anonymousAuthAxios.interceptors.request.use(config => {
  if (config.headers.common) delete config.headers.common.Authorization
  delete config.headers.Authorization
  delete config.headers.authorization

  return config
})

export const sessionControlAxios = axios.create({
  baseURL: cfAxios.defaults.baseURL,
  timeout: 15000
})
sessionControlAxios.interceptors.request.use(config => {
  if (config.headers.common) delete config.headers.common.Authorization

  return config
})
